import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ArrowDown, ArrowUp, Printer, RefreshCw, Save, Share2, ShoppingCart } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ProductRecommendationCard } from './ProductRecommendationCard';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface PackingListDisplayProps {
  blogPostId: string;
}

export const PackingListDisplay = ({ blogPostId }: PackingListDisplayProps) => {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [totalProgress, setTotalProgress] = useState(0);
  const isMobile = useIsMobile();
  
  const { data: packingListData, isLoading } = useQuery({
    queryKey: ['blogPostPackingList', blogPostId],
    queryFn: async () => {
      const { data: linkData, error: linkError } = await supabase
        .from('blog_packing_list_links')
        .select('packing_list_id')
        .eq('blog_post_id', blogPostId)
        .maybeSingle();
        
      if (linkError || !linkData) return null;
      
      const { data: packingList, error: packingListError } = await supabase
        .from('packing_lists')
        .select(`
          *,
          packing_list_items(
            id, name, category, is_essential, display_order
          ),
          product_recommendations(
            id, name, description, price, currency, image_url, affiliate_link, category, display_order
          )
        `)
        .eq('id', linkData.packing_list_id)
        .maybeSingle();
        
      if (packingListError || !packingList) return null;
      
      return packingList;
    }
  });
  
  useEffect(() => {
    if (!packingListData?.id) return;
    
    const storageKey = `packing-list-${packingListData.id}`;
    const savedItems = localStorage.getItem(storageKey);
    
    if (savedItems) {
      try {
        setCheckedItems(JSON.parse(savedItems));
      } catch (e) {
        console.error('Failed to parse saved packing list state', e);
      }
    }
  }, [packingListData?.id]);
  
  useEffect(() => {
    if (!packingListData?.id) return;
    
    const storageKey = `packing-list-${packingListData.id}`;
    localStorage.setItem(storageKey, JSON.stringify(checkedItems));
    
    if (packingListData.packing_list_items && packingListData.packing_list_items.length > 0) {
      const totalItems = packingListData.packing_list_items.length;
      const checkedCount = Object.values(checkedItems).filter(Boolean).length;
      setTotalProgress(Math.round((checkedCount / totalItems) * 100));
    }
  }, [checkedItems, packingListData]);
  
  if (isLoading) {
    return (
      <Card className="mt-6">
        <CardContent className="pt-6">
          <div className="h-20 flex items-center justify-center">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!packingListData) {
    return null;
  }
  
  const categorizedItems = packingListData.packing_list_items.reduce((acc, item) => {
    const category = item.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, any[]>);
  
  Object.keys(categorizedItems).forEach(category => {
    categorizedItems[category].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  });
  
  const sortedCategories = Object.keys(categorizedItems).sort((a, b) => {
    const aHasEssential = categorizedItems[a].some(item => item.is_essential);
    const bHasEssential = categorizedItems[b].some(item => item.is_essential);
    
    if (aHasEssential && !bHasEssential) return -1;
    if (!aHasEssential && bHasEssential) return 1;
    return a.localeCompare(b);
  });
  
  const getCategoryProgress = (category: string) => {
    const items = categorizedItems[category];
    const checkedCount = items.filter(item => checkedItems[item.id]).length;
    return `${checkedCount}/${items.length}`;
  };
  
  const toggleItem = (id: string) => {
    setCheckedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  const resetChecklist = () => {
    setCheckedItems({});
    toast.success("Checklist reset successfully");
  };
  
  const printPackingList = () => {
    window.print();
  };
  
  const savePackingList = () => {
    if (!packingListData?.id) return;
    
    const storageKey = `packing-list-${packingListData.id}`;
    localStorage.setItem(storageKey, JSON.stringify(checkedItems));
    toast.success("Progress saved successfully!");
  };
  
  const sharePackingList = () => {
    if (navigator.share) {
      navigator.share({
        title: `Packing List: ${packingListData.title}`,
        text: `Check out this packing list for ${packingListData.title}`,
        url: window.location.href,
      })
      .then(() => toast.success("Shared successfully"))
      .catch((error) => console.error('Error sharing:', error));
    } else {
      navigator.clipboard.writeText(window.location.href)
        .then(() => toast.success("Link copied to clipboard"))
        .catch(() => toast.error("Failed to copy link"));
    }
  };
  
  const sortedRecommendations = packingListData.product_recommendations
    ? [...packingListData.product_recommendations].map(product => ({
        ...product,
        source: 'recommendations' as const
      })).sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    : [];
  
  return (
    <Card className="mt-6 print:shadow-none packing-list-container">
      <CardHeader className={isMobile ? "px-4 py-5" : ""}>
        <CardTitle className="flex items-center gap-2 packing-list-header">
          <Check className="h-5 w-5" />
          <span className={cn(isMobile ? "text-lg" : "text-xl")}>What to Pack: {packingListData.title}</span>
        </CardTitle>
        {packingListData.description && (
          <p className="text-muted-foreground mt-1.5">{packingListData.description}</p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
          {packingListData.destination_type && (
            <Badge variant="outline">{packingListData.destination_type}</Badge>
          )}
          
          <TooltipProvider delayDuration={300}>
            <div className={`flex ${isMobile ? 'flex-wrap gap-3 justify-end mt-2' : 'gap-2'} items-center print:hidden no-print`}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size={isMobile ? "icon" : "sm"} 
                    onClick={resetChecklist} 
                    className={cn("h-12 w-12", isMobile ? "" : "min-w-[80px]")}
                  >
                    <RefreshCw className={cn("h-5 w-5", isMobile ? "" : "mr-2")} />
                    <span className={isMobile ? 'sr-only' : ''}>Reset</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Reset checklist</TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size={isMobile ? "icon" : "sm"} 
                    onClick={savePackingList} 
                    className={cn("h-12 w-12", isMobile ? "" : "min-w-[80px]")}
                  >
                    <Save className={cn("h-5 w-5", isMobile ? "" : "mr-2")} />
                    <span className={isMobile ? 'sr-only' : ''}>Save</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Save progress</TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size={isMobile ? "icon" : "sm"} 
                    onClick={printPackingList} 
                    className={cn("h-12 w-12", isMobile ? "" : "min-w-[80px]")}
                  >
                    <Printer className={cn("h-5 w-5", isMobile ? "" : "mr-2")} />
                    <span className={isMobile ? 'sr-only' : ''}>Print</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Print checklist</TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size={isMobile ? "icon" : "sm"} 
                    onClick={sharePackingList} 
                    className={cn("h-12 w-12", isMobile ? "" : "min-w-[80px]")}
                  >
                    <Share2 className={cn("h-5 w-5", isMobile ? "" : "mr-2")} />
                    <span className={isMobile ? 'sr-only' : ''}>Share</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Share checklist</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>
        
        <div className="mt-4 print:hidden no-print">
          <div className="flex justify-between text-sm mb-1.5">
            <span>Overall progress</span>
            <span>{totalProgress}%</span>
          </div>
          <Progress value={totalProgress} className={cn("h-3")} />
        </div>
      </CardHeader>
      <CardContent className={cn("space-y-6", isMobile ? "px-3 pt-0" : "")}>
        <div className="space-y-4">
          {sortedCategories.map(category => (
            <Collapsible 
              key={category}
              defaultOpen={true}
              open={expandedCategories[category] !== false}
              onOpenChange={() => toggleCategory(category)}
              className="print:!open packing-list-section"
            >
              <CollapsibleTrigger className={cn(
                "flex items-center justify-between w-full rounded-md border px-4 py-2.5 font-medium packing-list-category",
                isMobile && "py-3"
              )}>
                <div className="flex items-center gap-2">
                  <span className={isMobile ? "text-sm" : ""}>{category}</span>
                  <Badge variant="secondary" className="ml-2">
                    {getCategoryProgress(category)}
                  </Badge>
                </div>
                <div className="print:hidden no-print border rounded-full p-1 bg-accent/50">
                  {expandedCategories[category] === false ? (
                    <ArrowDown className="h-4 w-4" />
                  ) : (
                    <ArrowUp className="h-4 w-4" />
                  )}
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className={cn("pt-3 pl-2", isMobile && "pt-3")}>
                <div className="space-y-2 transition-all animate-accordion-down">
                  {categorizedItems[category].map(item => (
                    <div 
                      key={item.id} 
                      className={cn(
                        "flex items-center space-x-3 group packing-list-item hover:bg-accent/30 rounded-md p-1.5 pl-2",
                        isMobile && "py-3 px-2"
                      )}
                    >
                      <Checkbox 
                        id={`item-${item.id}`} 
                        checked={!!checkedItems[item.id]}
                        onCheckedChange={() => toggleItem(item.id)}
                        className={cn(
                          "transition-all duration-300 packing-list-checkbox focus:ring-2 focus:ring-primary",
                          isMobile && "h-6 w-6 min-h-[24px] min-w-[24px]"
                        )}
                      />
                      <label 
                        htmlFor={`item-${item.id}`}
                        className={cn(
                          "text-sm cursor-pointer transition-all duration-300 packing-list-label flex-1 py-1.5 text-left", 
                          checkedItems[item.id] ? 'line-through text-muted-foreground' : '',
                          isMobile && "text-sm py-0.5 pl-1.5"
                        )}
                      >
                        {item.name}
                        {item.is_essential && (
                          <Badge variant="warning" className="ml-2 text-xs py-0.5">
                            Essential
                          </Badge>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
        
        {sortedRecommendations.length > 0 && (
          <>
            <Separator className={cn(isMobile && "my-4")} />
            <div className="space-y-4">
              <h3 className={cn("text-lg font-semibold", isMobile && "px-1 text-base")}>Recommended Products</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sortedRecommendations.map(product => (
                  <ProductRecommendationCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

